import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NoteAlt as NoteIcon,
  CalendarMonth as CalIcon,
} from '@mui/icons-material';
import useStore from '../store/useStore';

const todayISO = () => new Date().toISOString().split('T')[0];

const SUGGESTED_TAGS = [
  'Словарь',
  'Грамматика',
  'Чтение',
  'Письмо',
  'Говорение',
  'Фонетика',
];

export default function LessonNotes({ studentId, isReadOnly }) {
  const { language } = useStore();
  const isRu = language === 'ru';

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editNote, setEditNote] = useState(null); // null = new, object = editing
  const [formDate, setFormDate] = useState(todayISO());
  const [formText, setFormText] = useState('');
  const [formTags, setFormTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/students/${studentId}/notes`);
      if (res.ok) setNotes(await res.json());
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditNote(null);
    setFormDate(todayISO());
    setFormText('');
    setFormTags([]);
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditNote(note);
    setFormDate(note.date ? note.date.split('T')[0] : todayISO());
    setFormText(note.text);
    setFormTags(note.tags || []);
    setDialogOpen(true);
  };

  const toggleTag = (tag) => {
    setFormTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSave = async () => {
    if (!formText.trim()) return;
    setSaving(true);
    try {
      const url = editNote
        ? `/api/students/${studentId}/notes/${editNote._id}`
        : `/api/students/${studentId}/notes`;
      const method = editNote ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: formDate,
          text: formText,
          tags: formTags,
        }),
      });
      if (res.ok) {
        setDialogOpen(false);
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`/api/students/${studentId}/notes/${id}`, { method: 'DELETE' });
    setDeleteId(null);
    await load();
  };

  // Group notes by month
  const grouped = notes.reduce((acc, note) => {
    const d = new Date(note.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(isRu ? 'ru-RU' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
    if (!acc[key]) acc[key] = { label, items: [] };
    acc[key].items.push(note);
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <NoteIcon color="primary" />
          {isRu ? 'Заметки об уроках' : 'Lesson Notes'}
        </Typography>
        {!isReadOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={openNew}
          >
            {isRu ? 'Добавить' : 'Add Note'}
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : notes.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <NoteIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {isRu ? 'Заметок пока нет' : 'No notes yet'}
          </Typography>
          {!isReadOnly && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={openNew}
            >
              {isRu ? 'Написать первую заметку' : 'Write the first note'}
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={3}>
          {Object.keys(grouped)
            .sort((a, b) => b.localeCompare(a))
            .map((monthKey) => (
              <Box key={monthKey}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  {grouped[monthKey].label}
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {grouped[monthKey].items.map((note) => (
                    <Paper
                      key={note._id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderLeft: '4px solid',
                        borderLeftColor: 'primary.main',
                        '&:hover': { boxShadow: 2 },
                        transition: 'box-shadow 0.2s',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5,
                            }}
                          >
                            <CalIcon
                              sx={{ fontSize: 14, color: 'text.disabled' }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(note.date).toLocaleDateString(
                                isRu ? 'ru-RU' : 'en-US',
                                {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                }
                              )}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                          >
                            {note.text}
                          </Typography>
                          {note.tags?.length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mt: 1,
                              }}
                            >
                              {note.tags.map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                        {!isReadOnly && (
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                            <Tooltip title={isRu ? 'Редактировать' : 'Edit'}>
                              <IconButton
                                size="small"
                                onClick={() => openEdit(note)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isRu ? 'Удалить' : 'Delete'}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteId(note._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ))}
        </Stack>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editNote
            ? isRu
              ? 'Редактировать заметку'
              : 'Edit Note'
            : isRu
              ? 'Новая заметка'
              : 'New Note'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={isRu ? 'Дата урока' : 'Lesson Date'}
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayISO() }}
              fullWidth
              size="small"
            />
            <TextField
              label={isRu ? 'Заметка' : 'Note'}
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              placeholder={
                isRu
                  ? 'Что прошли на уроке? Что получилось, что вызвало затруднение?'
                  : 'What was covered? What went well, what was difficult?'
              }
            />
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {isRu ? 'Темы' : 'Tags'}
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {SUGGESTED_TAGS.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onClick={() => toggleTag(tag)}
                    color={formTags.includes(tag) ? 'primary' : 'default'}
                    variant={formTags.includes(tag) ? 'filled' : 'outlined'}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {isRu ? 'Отмена' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formText.trim()}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : isRu ? (
              'Сохранить'
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{isRu ? 'Удалить заметку?' : 'Delete note?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isRu ? 'Это действие нельзя отменить.' : 'This cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>
            {isRu ? 'Отмена' : 'Cancel'}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDelete(deleteId)}
          >
            {isRu ? 'Удалить' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
